import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, X, Compass, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, PageHeader } from "@/components/common";
import IdeaCard from "@/components/IdeaCard";

const TOGGLES = [["beginner", "Beginner friendly"], ["low_cost", "Low startup cost"], ["weekend", "Weekend possible"], ["work_from_home", "Work from home"],
  ["low_barrier", "Low-skill / low entry"], ["high_margin", "High margin"], ["service", "Service"], ["product", "Product"], ["online", "Online"], ["local", "Local"]];
const BUDGETS = [["", "Any budget"], ["5000", "Under Rs 5,000"], ["10000", "Under Rs 10,000"], ["25000", "Under Rs 25,000"], ["50000", "Under Rs 50,000"], ["50000+", "Rs 50,000+"]];
const REVENUES = [["", "Any revenue potential"], ["0-20000", "Under Rs 20,000 / month"], ["20000-50000", "Rs 20,000–50,000 / month"], ["50000-100000", "Rs 50,000–100,000 / month"], ["100000-", "Rs 100,000+ / month"]];
const TIMES = [["", "Any"], ["evenings", "Evenings"], ["weekend", "Weekends"], ["flexible", "Flexible"], ["full_time", "Full-time potential"]];
const SELECTS = [["category", "Category"], ["difficulty", "Difficulty"], ["margin", "Margin potential"],
  ["competition", "Competition"], ["first_customer_difficulty", "First customer difficulty"]];
const budgetOk = (i, b) => !b || (b === "50000+" ? (i.startup_low ?? 0) >= 50000 : (i.startup_low ?? 0) <= Number(b));
const revenueOk = (i, r) => {
  if (!r) return true;
  const [lo, hi] = r.split("-").map((x) => (x ? Number(x) : null));
  const v = i.estimated_monthly_revenue_high ?? 0;
  return (lo == null || v > lo) && (hi == null || v <= hi);
};

function Select({ label, value, options, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[#5C6B73]">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full h-11 rounded-xl border border-[#EADEC9] bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1DB99A]">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

export default function Ideas() {
  const { t } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [f, setF] = useState({ budget: "", show: "all" });
  const [tog, setTog] = useState({});
  const [open, setOpen] = useState(false);
  const [cmp, setCmp] = useState([]);
  const maxCompare = 3;

  useEffect(() => { api.get("/ideas").then((r) => setData(r.data)); }, []);

  const opts = useMemo(() => {
    const o = {};
    SELECTS.forEach(([k]) => { o[k] = [...new Set((data?.ideas || []).map((i) => i[k]).filter(Boolean))].sort(); });
    return o;
  }, [data]);

  const list = useMemo(() => {
    if (!data) return [];
    const ql = q.trim().toLowerCase();
    return data.ideas.filter((i) =>
      (!ql || `${i.name} ${i.category} ${i.problem}`.toLowerCase().includes(ql)) &&
      SELECTS.every(([k]) => !f[k] || i[k] === f[k]) &&
      (budgetOk(i, f.budget)) && revenueOk(i, f.revenue) && (!f.time || i.tags?.[f.time]) &&
      Object.entries(tog).every(([k, v]) => !v || i.tags?.[k]) &&
      (f.show === "all" || (f.show === "saved" ? data.saved : data.favorites).includes(i.number)));
  }, [data, q, f, tog]);

  const toggle = async (number, kind) => {
    const { data: r } = await api.post(`/ideas/${number}/toggle/${kind}`);
    const key = kind === "saved" ? "saved" : "favorites";
    setData((d) => ({ ...d, [key]: r.active ? [...d[key], number] : d[key].filter((n) => n !== number) }));
    toast.success(r.active ? (kind === "saved" ? "Idea saved" : "Added to favorites") : "Removed");
  };
  const compare = (n) => setCmp((c) => c.includes(n) ? c.filter((x) => x !== n) : c.length >= maxCompare ? (toast.info(`Compare up to ${maxCompare} ideas`), c) : [...c, n]);
  const activeCount = SELECTS.filter(([k]) => f[k]).length + (f.budget ? 1 : 0) + (f.revenue ? 1 : 0) + (f.time ? 1 : 0) + Object.values(tog).filter(Boolean).length;
  const reset = () => { setF({ budget: "", show: f.show }); setTog({}); };

  if (!data) return <Loader />;
  return (
    <div>
      <PageHeader eyebrow={`Sidebizz ${t("Ideas")}`} title={t("Find a Side Business You Can Actually Start")} subtitle={t("Explore 150 practical business ideas adapted to Mauritius.")} />
      <Link to="/find" className="mb-4 flex items-center gap-4 rounded-2xl bg-[#1E2B3C] text-white p-4 sm:p-5 hover:bg-[#2A3C52] transition-colors" data-testid="find-my-business-cta">
        <span className="h-11 w-11 shrink-0 rounded-xl bg-[#1DB99A] flex items-center justify-center"><Compass className="h-5 w-5" /></span>
        <div className="flex-1"><div className="font-display font-extrabold">FIND MY BUSINESS</div><div className="text-xs text-white/60">Answer 6 quick questions and see ideas that may fit you.</div></div>
        <ArrowRight className="h-5 w-5 text-[#1DB99A]" />
      </Link>
      <div className="sticky top-14 md:top-0 z-30 -mx-4 px-4 py-3 bg-[#FBF8F2]/95 backdrop-blur-md space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8C9BA5]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ideas, e.g. car cleaning" data-testid="ideas-search-input"
              className="w-full h-12 rounded-xl border border-[#EADEC9] bg-white pl-10 pr-3 text-base focus:outline-none focus:ring-2 focus:ring-[#1DB99A]" />
          </div>
          <button onClick={() => setOpen(true)} className="sb-btn-navy px-4 relative" data-testid="ideas-filters-btn">
            <SlidersHorizontal className="h-4 w-4" /><span className="hidden sm:inline">Filters</span>
            {activeCount > 0 && <span className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-[#1DB99A] text-[10px] flex items-center justify-center">{activeCount}</span>}
          </button>
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {[["all", `All (${data.ideas.length})`], ["saved", `Saved (${data.saved.length})`], ["favorites", `Favorites (${data.favorites.length})`]].map(([k, l]) => (
            <button key={k} onClick={() => setF({ ...f, show: k })} data-testid={`ideas-show-${k}`}
              className={`shrink-0 rounded-full px-4 h-9 text-xs font-bold border ${f.show === k ? "bg-[#1E2B3C] text-white border-[#1E2B3C]" : "bg-white border-[#EADEC9] text-[#5C6B73]"}`}>{l}</button>
          ))}
          {TOGGLES.slice(0, 4).map(([k, l]) => (
            <button key={k} onClick={() => setTog({ ...tog, [k]: !tog[k] })} data-testid={`ideas-quick-${k}`}
              className={`shrink-0 rounded-full px-4 h-9 text-xs font-bold border ${tog[k] ? "bg-[#E8F8F4] border-[#1DB99A] text-[#179B80]" : "bg-white border-[#EADEC9] text-[#5C6B73]"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="text-xs text-[#8C9BA5] my-3" data-testid="ideas-count">{list.length} ideas</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ideas-grid">
        {list.map((i) => <IdeaCard key={i.number} idea={i} saved={data.saved.includes(i.number)} fav={data.favorites.includes(i.number)} comparing={cmp.includes(i.number)} onToggle={toggle} onCompare={compare} />)}
      </div>
      {!list.length && <div className="sb-card p-10 text-center text-sm text-[#5C6B73]">No ideas match these filters. <button className="text-[#179B80] font-semibold" onClick={reset}>Reset filters</button></div>}

      {cmp.length > 0 && (
        <div className="fixed bottom-[84px] md:bottom-6 inset-x-4 md:left-[calc(16rem+2rem)] md:right-8 z-40 fade-up" data-testid="compare-bar">
          <div className="max-w-3xl mx-auto rounded-2xl bg-[#1E2B3C] text-white p-3 pl-5 flex items-center gap-3 shadow-xl">
            <span className="text-sm font-semibold flex-1">{cmp.length} selected to compare</span>
            <button onClick={() => setCmp([])} className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center" data-testid="compare-clear-btn"><X className="h-4 w-4" /></button>
            <button disabled={cmp.length < 2} onClick={() => nav(`/compare?ids=${cmp.join(",")}`)} className="sb-btn-primary min-h-[40px] text-sm" data-testid="compare-go-btn">Compare</button>
          </div>
        </div>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md bg-[#FBF8F2] overflow-y-auto" data-testid="ideas-filter-sheet">
          <SheetHeader><SheetTitle className="font-display text-xl">Filters</SheetTitle></SheetHeader>
          <div className="space-y-4 mt-6">
            <Select label="Startup cost" value={f.budget} options={BUDGETS} onChange={(v) => setF({ ...f, budget: v })} testid="filter-budget" />
            <Select label="Monthly revenue potential" value={f.revenue || ""} options={REVENUES} onChange={(v) => setF({ ...f, revenue: v })} testid="filter-revenue" />
            <Select label="Time" value={f.time || ""} options={TIMES} onChange={(v) => setF({ ...f, time: v })} testid="filter-time" />
            {SELECTS.map(([k, l]) => <Select key={k} label={l} value={f[k] || ""} options={[["", "Any"], ...opts[k].map((v) => [v, v])]} onChange={(v) => setF({ ...f, [k]: v })} testid={`filter-${k}`} />)}
            <div className="flex flex-wrap gap-2 pt-2">
              {TOGGLES.map(([k, l]) => (
                <button key={k} onClick={() => setTog({ ...tog, [k]: !tog[k] })} data-testid={`filter-toggle-${k}`}
                  className={`rounded-full px-3.5 h-9 text-xs font-bold border ${tog[k] ? "bg-[#E8F8F4] border-[#1DB99A] text-[#179B80]" : "bg-white border-[#EADEC9] text-[#5C6B73]"}`}>{l}</button>
              ))}
            </div>
            <div className="flex gap-2 pt-4 pb-safe">
              <button className="sb-btn-ghost flex-1" onClick={reset} data-testid="filter-reset-btn">Reset</button>
              <button className="sb-btn-primary flex-1" onClick={() => setOpen(false)} data-testid="filter-apply-btn">Show {list.length} ideas</button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
